import { cn } from "@faire/prototype-playground/lib/utils";
import { ChatBubble } from "@faire/slate-icons/ChatBubble";
import { Heart } from "@faire/slate-icons/Heart";
import { HorizontalEllipsis } from "@faire/slate-icons/HorizontalEllipsis";
import { Market } from "@faire/slate-icons/Market";
import { SaleTag } from "@faire/slate-icons/SaleTag";
import { StarFilled } from "@faire/slate-icons/StarFilled";
import { SVGIconProps } from "@faire/slate-icons/types";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { Menu, MenuItem, MenuTrigger } from "@faire/slate/components/menu";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { motion, useReducedMotion } from "framer-motion";
import React, { useEffect, useState } from "react";

import { CREAM, PADX } from "../constants";
import { type BrandDataset } from "../types";
import { useBrandLogo } from "../useBrandLogo";

/** The single line the hero states when a page passes no `incentives`. */
const DEFAULT_INCENTIVE =
  "Get 60 days to pay and free returns on your first order";

export type MastheadVariant = "redesigned" | "current" | "full-bleed";

// How the brand name renders in the redesigned hero: the logotype image, or the
// name set in a Slate type token (page-heading = sans, display-s = serif).
export type BrandNameStyle = "wordmark" | "sans" | "serif";

/**
 * How much of the brand's own identity the Faire-side header carries — a
 * deliberate intensity ramp rather than a set of unrelated looks, so the
 * question "how branded should a brand page be?" can be answered by sliding
 * one dial (Ryan, 2026-08-17).
 *
 *   "generic"   — no brand color at all. Plain white band, dark text, wordmark
 *                 and meta only. The free-tier floor: Faire's page, their name.
 *   "brand"     — their color fills the band, photography in the side panel.
 *                 Today's default, and the middle of the ramp.
 *   "immersive" — their photography spans the full header with the identity
 *                 over a scrim. The most brand, and the closest the Faire
 *                 surface gets to their own site without becoming it.
 */
export type MastheadTheme = "generic" | "brand" | "immersive";

/**
 * Toolbar states for the intensity ramp, shared so every brand page offers the
 * same three steps in the same order and a shared `?states=identity:N` URL
 * means the same thing on all of them.
 */
export const BRAND_IDENTITY_STATES = [
  { stateNumber: 1, label: "None — Faire neutral" },
  { stateNumber: 2, label: "Brand color" },
  { stateNumber: 3, label: "Immersive photo" },
];

export const BRAND_IDENTITY_THEMES: Record<number, MastheadTheme> = {
  1: "generic",
  2: "brand",
  3: "immersive",
};

interface MastheadProps {
  brand: BrandDataset;
  variant: MastheadVariant;
  /** Logged-out surfaces the Faire sign-up value-prop line in the hero. */
  isLoggedOut: boolean;
  /**
   * The Faire incentives stated in the logged-out hero, replacing the single
   * default line. Two or more rotate on a dissolve; one renders statically.
   *
   * Exists so a brand page can tell the same story here that it tells in a
   * banner above its own site header, instead of the two surfaces quoting
   * different halves of one offer (Ryan, 2026-09-02).
   */
  incentives?: string[];
  /** Brand-name treatment in the redesigned hero. Defaults to the wordmark. */
  nameStyle?: BrandNameStyle;
  /** Redesigned-hero color treatment. Defaults to the brand's own color. */
  theme?: MastheadTheme;
  /** Where the "..." overflow menu's prototype nav aid links to (e.g. a brand.com
   * router-entry surface). Omit to hide that menu item entirely — most brands
   * piped through this framework won't have an equivalent entry surface. */
  entryRoute?: string;
  /** "full-bleed" only: strip the Faire marketplace furniture (rating/minimum
   * meta, promo card, favorite/message/overflow) so the hero reads as the
   * brand's own site — just the wordmark, larger, top-left. */
  chromeless?: boolean;
  /** Fires from the full-bleed hero's campaign CTA (see
   * `BrandDataset.landingHeroCtaLabel`). */
  onHeroCtaClick?: () => void;
  /** "full-bleed" + `chromeless` only: suppress the hero's own wordmark, for
   * when the page already renders a brand-site header that carries it. */
  showWordmark?: boolean;
  /** Redesigned hero, logged out: where the Faire sign-up value prop goes.
   *   "band"  — a tinted band inside the brand-color panel, with a Sign up
   *             button (the original, and still the default).
   *   "strip" — a full-width strip across the bottom of the whole header
   *             module (under the hero image too), statement only, at
   *             paragraph size. The band was competing with the brand's own
   *             identity inside its own color; the strip states the offer
   *             once, quietly, and lets the panel shrink (Ryan, 2026-08-18).
   */
  valuePropPlacement?: "band" | "strip";
  /** Redesigned hero: how the header divides between the brand-color panel and
   * the photo on desktop.
   *   "default" — 58/42 in the color panel's favor (today's proportions).
   *   "even"    — a true 50/50 split, for testing a more photo-forward header.
   * Mobile stacks either way, so this is desktop-only. */
  imageSplit?: "default" | "even";
  /**
   * Redesigned hero: pin the photograph to 16:9 and the last four columns of
   * the page grid (same crop the Faire Direct header uses). The color panel
   * gets the remaining eight columns, which is what gives promo cards room to
   * sit on one line. Omit to keep the 58/42 or 50/50 fluid split.
   */
  photoAspect?: "16/9";
  /** "strip" only — the strip's background. Defaults to the brand's secondary
   * surface; pass a warmer/deeper (or, for maximum contrast against a pale
   * brand color, a much darker) alternative when that secondary is too
   * near-white to register as its own band. Text color follows automatically:
   * white on a dark strip, primary ink on a light one. */
  valuePropStripColor?: string;
}

export const Masthead: React.FC<MastheadProps> = ({
  brand,
  variant,
  isLoggedOut,
  incentives,
  nameStyle = "wordmark",
  theme = "brand",
  entryRoute,
  chromeless = false,
  showWordmark = true,
  onHeroCtaClick,
  valuePropPlacement = "band",
  valuePropStripColor,
  imageSplit = "default",
  photoAspect,
}) => {
  if (variant === "current") {
    return <CurrentHeader brand={brand} entryRoute={entryRoute} />;
  }
  if (variant === "full-bleed") {
    return (
      <FullBleedHero
        brand={brand}
        isLoggedOut={isLoggedOut}
        nameStyle={nameStyle}
        incentives={incentives}
        entryRoute={entryRoute}
        chromeless={chromeless}
        showWordmark={showWordmark}
        onHeroCtaClick={onHeroCtaClick}
      />
    );
  }
  return (
    <RedesignedHero
      brand={brand}
      isLoggedOut={isLoggedOut}
      nameStyle={nameStyle}
      incentives={incentives}
      theme={theme}
      entryRoute={entryRoute}
      valuePropPlacement={valuePropPlacement}
      valuePropStripColor={valuePropStripColor}
      imageSplit={imageSplit}
      photoAspect={photoAspect}
    />
  );
};

const MetaDot: React.FC = () => (
  <span aria-hidden className="opacity-60">
    ·
  </span>
);

// Transparent 40px icon button matching the global-nav buttons. Darker hover
// on both the maroon and cream headers — a light fill disappeared on cream.
const ICON_HOVER =
  "hover:bg-(--slate-color-grey-1000)/[0.12]";

const ActionButton: React.FC<{
  Icon: React.FC<SVGIconProps>;
  label: string;
  onDark?: boolean;
}> = ({ Icon, label, onDark = false }) => (
  <button
    type="button"
    aria-label={label}
    className={cn(
      "flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent transition-colors duration-300 ease-in-out",
      ICON_HOVER
    )}
  >
    <Icon
      className="h-5 w-5 [stroke-width:1.5]"
      color={onDark ? "#ffffff" : undefined}
    />
  </button>
);

// "..." trigger for the hero — white glyph on the brand-color band, default
// glyph on the generic (free-tier) neutral band. forwardRef so Slate's
// MenuTrigger can wire up open/close + floating positioning on a custom element.
const HeroMoreTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<"button"> & { onDark?: boolean }
>(({ className, onDark = true, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label="More"
    className={cn(
      "flex h-10 w-10 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent transition-colors duration-300 ease-in-out",
      ICON_HOVER,
      className
    )}
    {...props}
  >
    <HorizontalEllipsis
      className="h-5 w-5 [stroke-width:1.5]"
      color={onDark ? "#ffffff" : undefined}
    />
  </button>
));
HeroMoreTrigger.displayName = "HeroMoreTrigger";

// "..." overflow menu — the trigger icon always renders (it's a standard part
// of this header's icon row, present regardless of brand), but its one real
// option — jumping to the brand's own entry-router surface, e.g. a brand.com
// router page — only appears when the caller supplies an `entryRoute`. Most
// brands piped through this framework won't have an equivalent surface yet;
// the icon still shows so the row's layout doesn't shift brand to brand.
const WholesaleMenu: React.FC<{
  trigger: React.ReactNode;
  entryRoute?: string;
}> = ({ trigger, entryRoute }) => {
  if (!entryRoute) return <>{trigger}</>;
  return (
    <Menu aria-label="More options">
      <MenuTrigger>{trigger}</MenuTrigger>
      <MenuItem label="Prototype: Apply for Wholesale" linkTo={entryRoute} />
    </Menu>
  );
};

// Icon for a promo card, picked by keyword so any brand's promo copy (or a
// future Faire Market banner) gets a sensible glyph without per-brand data.
const promoIcon = (promo: string): React.FC<SVGIconProps> => {
  const lowerPromo = promo.toLowerCase();
  if (lowerPromo.includes("shipping") || lowerPromo.includes("insider")) {
    return Insider;
  }
  if (lowerPromo.includes("market")) return Market;
  return SaleTag;
};

// A brand promo (offer, policy, or — eventually — a Faire Market banner),
// rendered as an elevated card rather than a small pill: every brand shows
// 1-3 of these, so they need to read as a highlight, not a footnote. Always
// the same neutral surface regardless of the hero's own color/theme, like a
// card floating on top of the band.
const PromoCard: React.FC<{ promo: string }> = ({ promo }) => {
  const Icon = promoIcon(promo);
  const isInsider = Icon === Insider;
  return (
    <div className="border-fs-border-muted bg-fs-surface-primary flex w-fit items-center gap-2 rounded-lg border py-[9px] pr-5 pl-3">
      {isInsider ? (
        <Insider small width="20" height="20" className="flex-shrink-0" />
      ) : (
        <Icon
          width="20"
          height="20"
          className="text-fs-text-primary flex-shrink-0"
        />
      )}
      <span className="type-fs-paragraph-medium text-fs-text-primary whitespace-nowrap">
        {promo}
        {isInsider ? " with Insider" : null}
      </span>
    </div>
  );
};

const BrandWordmark: React.FC<{
  brand: BrandDataset;
  nameStyle: BrandNameStyle;
  textColor: string;
  onDark: boolean;
  /** Multiplies the brand's own `logoHeight` (and the text sizes) — used by
   * the chromeless full-bleed header, where the wordmark is the only thing on
   * the image and can carry more weight. A multiplier rather than a fixed
   * size so it scales off whatever each brand already tuned. */
  heightScale?: number;
}> = ({ brand, nameStyle, textColor, onDark, heightScale = 1 }) => {
  // `useBrandLogo` decides when there is no logotype to draw — no asset, or an
  // asset whose URL doesn't resolve — so this hero, the brand-forward header
  // and the sign-up modal all give up on a logo at the same point. A dead URL
  // used to render a broken-image glyph here instead of the name.
  const { hasLogo, onLogoError } = useBrandLogo(brand.logoSrc);
  // Wordmark: the brand's logotype image (falls back to the serif name when the
  // brand has no logo). Sans / Serif: the name set in a Slate type token — in
  // this system display-s is Graphik (sans) and page-heading is Nantes (serif).
  // No fixed-height row wrapper — the image/text sits at its own natural size,
  // same as the original PF Candle masthead; `logoHeight` is the per-brand knob
  // for fitting a wordmark PNG's own ink density/aspect ratio to that baseline.
  if (nameStyle === "wordmark" && hasLogo) {
    return (
      <img
        src={brand.logoSrc}
        alt={brand.name}
        onError={onLogoError}
        style={{
          height:
            heightScale === 1
              ? (brand.logoHeight ?? "24px")
              : `calc(${brand.logoHeight ?? "24px"} * ${heightScale})`,
          width: "auto",
          display: "block",
          alignSelf: "flex-start",
          // White version of the black logo for the dark hero; left as
          // authored (its own dark ink) on the light/generic hero.
          filter: onDark ? "brightness(0) invert(1)" : undefined,
        }}
      />
    );
  }
  // display-s carries Graphik (sans); page-heading carries Nantes (serif). Size
  // them explicitly — serif larger (30px), sans a touch smaller (24px) — since
  // Nantes only ships at the 22px page-heading token in this scale.
  const isSans = nameStyle === "sans";
  return (
    <span
      className={isSans ? "type-fs-display-s" : "type-fs-page-heading"}
      style={{
        color: textColor,
        marginTop: "6px",
        alignSelf: "flex-start",
        fontSize: isSans ? "24px" : "30px",
        lineHeight: 1.15,
      }}
    >
      {brand.name}
    </span>
  );
};

const RatingMeta: React.FC<{
  brand: BrandDataset;
  textColor: string;
  starColor?: string;
}> = ({ brand, textColor, starColor }) => (
  <div
    className="type-fs-paragraph flex flex-wrap items-center gap-2"
    style={{ color: textColor }}
  >
    {brand.hasPerformanceData ? (
      <>
        <span>Top Shop</span>
        <MetaDot />
      </>
    ) : null}
    {brand.rating ? (
      <>
        <span className="inline-flex items-center gap-1">
          {brand.rating}
          <StarFilled width="12" height="12" color={starColor} />
          {brand.reviewCount ? (
            <span className="underline">({brand.reviewCount})</span>
          ) : null}
        </span>
        <MetaDot />
      </>
    ) : (
      <>
        <span>New shop</span>
        <MetaDot />
      </>
    )}
    <span>{brand.location}</span>
    {brand.minOrder ? (
      <>
        <MetaDot />
        <span>${brand.minOrder} min</span>
      </>
    ) : null}
    {/* Performance signals: proof/stats about the brand (e.g. "Top 1%
        reordered in Candles & holders") — same meta-line category as the
        rating above it, not an offer, so it reads with the rest of this line
        rather than competing with the promo cards below. */}
    {(brand.performanceSignals ?? []).map((signal) => (
      <React.Fragment key={signal}>
        <MetaDot />
        <span className="inline-flex items-center gap-1">
          <StarFilled width="12" height="12" color={starColor} />
          {signal}
        </span>
      </React.Fragment>
    ))}
  </div>
);

/**
 * Full-bleed editorial hero — the brand's own landing-page header rather than
 * a Faire marketplace masthead: one wide lifestyle image running edge to edge,
 * with the brand identity laid over it. Pairs with `hideNav`/`hideFooter` to
 * preview a brand-owned storefront.
 *
 * 21:9 on desktop is the common web-hero ratio nearest most brand-composed
 * heroes' native aspect (Lemonade's real one is 2.40), so it crops almost
 * nothing. Taller 4:3 on mobile so the subject survives the narrow viewport.
 * This deliberately pushes merchandising below the fold — that's the point of
 * a landing page.
 *
 * Motion: a slow Ken Burns push on the image plus a staggered rise of the
 * overlaid identity. Both are `prefers-reduced-motion` aware via
 * `useReducedMotion`, and the image transform is GPU-only (scale), so it
 * doesn't thrash layout.
 */
const FullBleedHero: React.FC<{
  brand: BrandDataset;
  isLoggedOut: boolean;
  nameStyle: BrandNameStyle;
  /** See MastheadProps.incentives. */
  incentives?: string[];
  entryRoute?: string;
  /** Drop every piece of Faire marketplace furniture from the hero — the
   * rating/minimum/ship-window meta, the promo card, the favorite/message/
   * overflow actions — leaving just the brand's wordmark, larger, top-left.
   * For previewing this as the brand's own site rather than a Faire page. */
  chromeless?: boolean;
  /** Suppress the hero's wordmark when a brand-site header already shows it. */
  showWordmark?: boolean;
  /** Fires from `BrandDataset.landingHeroCtaLabel`'s button. */
  onHeroCtaClick?: () => void;
}> = ({
  brand,
  isLoggedOut,
  nameStyle,
  incentives,
  entryRoute,
  chromeless = false,
  showWordmark = true,
  onHeroCtaClick,
}) => {
  const reduceMotion = useReducedMotion();
  const imageUrl = brand.landingHeroImage ?? brand.heroImage;
  // Reduced motion falls back to the still: a looping video is exactly the kind
  // of continuous movement that setting is asking us not to play.
  const videoUrl = reduceMotion ? undefined : brand.landingHeroVideo;
  // Always the on-dark treatment: the identity sits over photography behind a
  // scrim, so white text/wordmark regardless of how light the brand color is.
  return (
    <section className="relative w-full overflow-hidden">
      <div className="relative aspect-[4/3] w-full md:aspect-[21/9]">
        {videoUrl ? (
          // The brand's own footage on loop. Muted + playsInline are what make
          // autoplay legal on iOS and in Chrome; the hero still is the poster,
          // so the first frame the reader sees is a photograph rather than a
          // black box while the file streams in.
          <video
            src={videoUrl}
            poster={imageUrl}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden
            // Anchored to the BOTTOM of the frame, not the center: the hero is a
            // 21:9 slot and the source is 16:9, so a third of the height gets
            // cropped — and a center crop cut the toes and soles off the very
            // shoes the film is selling. Cropping off the top takes leg and
            // backdrop instead, which costs nothing.
            className="absolute inset-0 h-full w-full object-cover object-bottom"
          />
        ) : imageUrl ? (
          <motion.img
            src={imageUrl}
            alt={`${brand.name} hero`}
            className="absolute inset-0 h-full w-full object-cover object-center"
            // A continuously breathing Ken Burns rather than a one-shot: it
            // keeps the header alive instead of settling, and because it
            // starts at rest (scale 1) the image never sits parked at an
            // arbitrary zoom if rAF is throttled (background tab) or motion
            // is reduced.
            animate={reduceMotion ? undefined : { scale: [1, 1.06] }}
            transition={
              reduceMotion
                ? undefined
                : {
                    duration: 18,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatType: "reverse",
                  }
            }
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: brand.brandColorDeep }}
          />
        )}
        {/* Scrim: bottom-weighted so the identity stays legible over any
            photograph without flattening the image's midtones. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.30) 72%, rgba(0,0,0,0.62) 100%)",
          }}
        />

        {/* Campaign line, dead center. Not a Slate type utility on purpose —
            those carry their own family, size and weight, and this needs the
            brand's own face at a fixed display size. The drop shadow is
            insurance, not decoration: the scrim is at its lightest here (5%)
            precisely where this sits, and moving footage means the pixels
            behind the letters change every frame. */}
        {brand.landingHeroHeadline ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-8 px-6">
            <p
              className={cn(
                "text-fs-text-primary-inverse text-center text-[120px] leading-none font-semibold tracking-[-0.02em] uppercase",
                "[text-shadow:0_2px_24px_rgba(0,0,0,0.35)]",
                // Optical kern on the first pair only. Poppins ExtraBold ships
                // no kern pair for F+A, and at 120px the F's flat right side
                // against the A's diagonal opens a hole the rest of the line
                // doesn't have. letter-spacing on ::first-letter closes exactly
                // that gap — tightening the whole line would crowd the LL and
                // the numerals instead.
                "[&::first-letter]:tracking-[-0.06em]"
              )}
            >
              {brand.landingHeroHeadline}
            </p>
            {/* pointer-events restored just for the button: the wrapper stays
                transparent to clicks so it can't swallow anything behind the
                hero. White fill because a Slate primary is near-black and this
                sits on footage that's dark half the loop. */}
            {brand.landingHeroCtaLabel ? (
              <span className="[&_button]:bg-fs-surface-primary pointer-events-auto [&_button]:text-(--slate-color-grey-1000)">
                <Button variant="secondary" onClick={onHeroCtaClick}>
                  {brand.landingHeroCtaLabel}
                </Button>
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Favorite / message / overflow are Faire marketplace actions, not
            the brand's own — they go with the rest of the chrome. */}
        {chromeless ? null : (
          <div className="absolute top-6 right-6 flex items-center gap-1">
            <ActionButton Icon={Heart} label="Favorite" onDark />
            <ActionButton Icon={ChatBubble} label="Message" onDark />
            <WholesaleMenu
              trigger={<HeroMoreTrigger onDark />}
              entryRoute={entryRoute}
            />
          </div>
        )}

        {/* Entrance reveal is a plain CSS animation with NO fill-mode, so the
            elements' base style is the visible resting state: if the
            animation never runs (throttled/background tab, reduced motion, a
            JS error), the brand's rating, minimum and ship window are still
            legible. A framer-motion `initial={{opacity:0}}` would leave them
            invisible in exactly those cases — not a trade worth making for
            content this load-bearing. */}
        <style>
          {
            "@keyframes fbHeroRise{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}"
          }
        </style>
        {chromeless ? (
          !showWordmark ? null : (
            // Brand-owned reading: the wordmark alone, top-left, scaled up to
            // carry the frame on its own now that nothing else sits on it.
            <div
              className={cn("absolute top-7 right-0 left-0", PADX)}
              style={
                reduceMotion
                  ? undefined
                  : { animation: "fbHeroRise 700ms cubic-bezier(0.16,1,0.3,1)" }
              }
            >
              <BrandWordmark
                brand={brand}
                nameStyle={nameStyle}
                textColor={CREAM}
                onDark
                heightScale={1.6}
              />
            </div>
          )
        ) : (
          <div
            className={cn(
              "absolute right-0 bottom-0 left-0 flex flex-col gap-3 pb-6 md:pb-9",
              PADX
            )}
            style={
              reduceMotion
                ? undefined
                : { animation: "fbHeroRise 700ms cubic-bezier(0.16,1,0.3,1)" }
            }
          >
            <BrandWordmark
              brand={brand}
              nameStyle={nameStyle}
              textColor={CREAM}
              onDark
            />
            <div className="flex flex-col gap-1.5">
              <RatingMeta brand={brand} textColor={CREAM} starColor="#ffffff" />
              <div
                className="type-fs-paragraph flex flex-wrap items-center gap-2"
                style={{ color: CREAM }}
              >
                <span>Get it by {brand.shipWindow}</span>
                <MetaDot />
                <button className="underline">Shipping details</button>
              </div>
            </div>
            {isLoggedOut ? (
              <div className="mt-1 flex flex-wrap items-center gap-4">
                <IncentiveLine
                  messages={incentives ?? [DEFAULT_INCENTIVE]}
                  className="type-fs-subheading"
                  color={CREAM}
                />
                <span data-signup-gate className="flex-shrink-0">
                  <Button variant="secondary" size="small">
                    Sign up
                  </Button>
                </span>
              </div>
            ) : brand.promos.length > 0 ? (
              <div className="mt-1 flex items-center gap-2 overflow-x-auto [scrollbar-width:none] md:flex-wrap [&::-webkit-scrollbar]:hidden">
                {brand.promos.slice(0, 3).map((promo) => (
                  <PromoCard key={promo} promo={promo} />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

const RedesignedHero: React.FC<{
  brand: BrandDataset;
  isLoggedOut: boolean;
  nameStyle: BrandNameStyle;
  /** See MastheadProps.incentives. */
  incentives?: string[];
  theme: MastheadTheme;
  entryRoute?: string;
  /** See MastheadProps.valuePropPlacement. */
  valuePropPlacement?: "band" | "strip";
  valuePropStripColor?: string;
  /** See MastheadProps.imageSplit. */
  imageSplit?: "default" | "even";
  /** See MastheadProps.photoAspect. */
  photoAspect?: "16/9";
}> = ({
  brand,
  isLoggedOut,
  nameStyle,
  incentives,
  theme,
  entryRoute,
  valuePropPlacement = "band",
  valuePropStripColor,
  imageSplit = "default",
  photoAspect,
}) => {
  // Generic (free-tier) theme drops the brand's own color entirely for a
  // neutral tertiary-grey band. A brand's OWN color can also be too light for
  // white/cream text (e.g. a pale blush) — in both cases the band gets the
  // light-background treatment (dark text) instead of assuming every brand
  // color is dark enough for white text.
  const isGeneric = theme === "generic";
  // Immersive puts photography behind the whole header, so its text is always
  // on a dark scrim regardless of how light the brand's color is.
  const isImmersive = theme === "immersive" && Boolean(brand.heroImage);
  const onDark =
    isImmersive || (!isGeneric && !isLightBrandColor(brand.brandColor));
  // Free tier is plain white, not a neutral grey — no custom brand color, but
  // no fabricated "brandless" tint either. Immersive keeps the brand color as
  // the backdrop the photo sits on, so any letterboxing reads as intentional.
  const heroBg = isGeneric
    ? "var(--slate-color-surface-primary)"
    : brand.brandColor;
  const heroTextColor = onDark ? CREAM : "var(--slate-color-text-primary)";
  // undefined lets the icon fall back to currentColor, inheriting heroTextColor
  // from its containing element — same idiom ActionButton/HeroMoreTrigger use.
  const starColor: string | undefined = onDark ? "#ffffff" : undefined;
  // Strip mode moves the sign-up value prop out of the brand-color panel and
  // onto its own full-width band below the whole module, so the panel no
  // longer has to reserve room for it and can sit shorter.
  const useValuePropStrip = valuePropPlacement === "strip";
  const stripColor =
    valuePropStripColor ??
    brand.secondaryColor ??
    "var(--slate-color-surface-tertiary)";
  const showValuePropBand = isLoggedOut && !useValuePropStrip;
  const showValuePropStrip = isLoggedOut && useValuePropStrip;
  const heroFade = "pfHomeFade 450ms ease-out both";

  const hero = (
    <section
      // The section is the flex container itself (not a `minHeight` box with
      // an `h-full` row inside) — a percentage height doesn't reliably
      // resolve against a `min-height`-only parent, so the old nesting left
      // the row (and the image + `mt-auto` band inside it) sized to content
      // rather than the section's real height. Flex items stretch to the
      // container's actual used height by default, so this keeps the image
      // and the bottom-aligned band correct whether content is shorter or
      // taller than the 300px floor.
      className="relative flex w-full flex-col overflow-hidden md:flex-row"
      style={{
        backgroundColor: heroBg,
        // 300px reserves room for the in-panel sign-up band; with the value
        // prop moved out to its own strip there is nothing to reserve, so the
        // panel sits shorter (per Ryan) and the strip carries the bottom edge.
        minHeight:
          photoAspect === "16/9"
            ? undefined
            : useValuePropStrip
              ? "250px"
              : "300px",
        animation: heroFade,
      }}
    >
      {/* Immersive: the brand's photography spans the whole header instead of
          sitting in a side panel, with a left-weighted scrim so the identity
          and meta stay legible over it. Sibling of the content rather than a
          background-image on the section, so `heroImagePosition` still aims
          the crop the same way it does in the panel. */}
      {isImmersive ? (
        <>
          <img
            src={brand.heroImage}
            alt={`${brand.name} hero`}
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: brand.heroImagePosition ?? "center" }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0.15) 100%)",
            }}
          />
        </>
      ) : null}
      <div
        className={cn(
          "relative flex flex-1 flex-col gap-3 pt-8 pb-4 md:pb-8 lg:pb-[36px] 2xl:pb-[64px]",
          PADX,
          // Extra breathing room against the hero image seam, beyond PADX's
          // symmetric inset (desktop two-column layout only).
          "md:pr-16 lg:pr-[64px] 2xl:pr-[96px]"
        )}
      >
        <div className="absolute top-7 right-8 flex items-center gap-1">
          <ActionButton Icon={Heart} label="Favorite" onDark={onDark} />
          <ActionButton Icon={ChatBubble} label="Message" onDark={onDark} />
          <WholesaleMenu
            trigger={<HeroMoreTrigger onDark={onDark} />}
            entryRoute={entryRoute}
          />
        </div>

        <BrandWordmark
          brand={brand}
          nameStyle={nameStyle}
          textColor={heroTextColor}
          onDark={onDark}
        />

        <div
          className={cn(
            "flex flex-col gap-1.5",
            // Value-prop band present: sit below the logo (the band anchors
            // the bottom). No band: drop down to fill the space.
            showValuePropBand ? "mt-8" : "mt-auto"
          )}
        >
          <RatingMeta
            brand={brand}
            textColor={heroTextColor}
            starColor={starColor}
          />
          <div
            className="type-fs-paragraph flex flex-wrap items-center gap-2"
            style={{ color: heroTextColor }}
          >
            <span>Get it by {brand.shipWindow}</span>
            <MetaDot />
            <button className="underline">Shipping details</button>
          </div>
        </div>

        {/* Promos: offers/policies the retailer gets (and, eventually, Faire
              Market banners) — every brand shows 1-3 as elevated cards, not
              small pills, so they read as a highlight worth a second look.
              Hidden when logged out: the Faire sign-up band below is the one
              thing competing for attention there, and (on desktop) it's
              absolutely positioned assuming nothing else occupies that space. */}
        {!isLoggedOut && brand.promos.length > 0 ? (
          <div className="mt-1 flex items-center gap-2 overflow-x-auto [scrollbar-width:none] md:flex-wrap [&::-webkit-scrollbar]:hidden">
            {brand.promos.slice(0, 3).map((promo) => (
              <PromoCard key={promo} promo={promo} />
            ))}
          </div>
        ) : null}

        {/* Faire value props + sign-up CTA (logged-out only) — a tinted band.
              `mt-auto` (not absolute positioning) pushes it to the bottom of
              the flex column: the content above it varies too much brand to
              brand (rating, performance signal, promo cards) to safely
              assume there's reserved empty space below it, but flexbox always
              computes the right position from whatever's actually there.
              50%-off benefit lives in the modal. */}
        {showValuePropBand ? (
          <div
            // Mobile: full-bleed in-flow block (‑mx cancels the column padding).
            // Desktop (md+): the band itself breaks out to the column's full
            // width and true bottom (edge to edge), but its own right
            // padding keeps the Sign up button's right edge aligned with
            // the heart/message/"..." icon row above it (`right-8`), not
            // flush with the true edge.
            className="-mx-4 mt-auto pr-8 pl-4 md:-mr-16 md:-mb-8 md:-ml-10 md:pr-8 md:pl-10 lg:-mr-[64px] lg:-mb-[36px] lg:-ml-[48px] lg:pr-8 lg:pl-[48px] 2xl:-mr-[96px] 2xl:-mb-[64px] 2xl:-ml-[80px] 2xl:pr-8 2xl:pl-[80px]"
            style={{
              backgroundColor: onDark ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.05)",
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-4 py-4">
              <IncentiveLine
                messages={incentives ?? [DEFAULT_INCENTIVE]}
                className="type-fs-subheading"
                color={heroTextColor}
              />
              <span data-signup-gate className="flex-shrink-0">
                <Button variant="secondary" size="small">
                  Sign up
                </Button>
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Right: brand hero image, or a neutral block for a brand with none yet.
          Immersive already spans the full width with that same image, so the
          panel would just repeat it. */}
      {isImmersive ? null : (
        <div
          className={cn(
            "relative w-full shrink-0",
            photoAspect === "16/9"
              ? [
                  "aspect-[16/9]",
                  "md:w-[calc((100%_-_256px)/3_+_88px)]",
                  "lg:w-[calc((100%_-_272px)/3_+_96px)]",
                  "2xl:w-[calc((100%_-_336px)/3_+_128px)]",
                ].join(" ")
              : [
                  "h-[220px] md:h-auto",
                  imageSplit === "even" ? "md:w-1/2" : "md:w-[42%]",
                ].join(" ")
          )}
        >
          {brand.heroImage ? (
            <img
              src={brand.heroImage}
              alt={`${brand.name} hero`}
              // Centre is the right default for a squared product shot, but wrong
              // for a wide page banner: those are usually composed with subject
              // matter at the ends and negative space in the middle, so
              // object-cover in this ~1.8:1 panel lands squarely on the empty
              // part. `heroImagePosition` lets a brand aim the crop.
              className="h-full w-full object-cover"
              style={{ objectPosition: brand.heroImagePosition ?? "center" }}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{
                backgroundColor: isGeneric
                  ? "var(--slate-color-surface-secondary)"
                  : brand.brandColorDeep,
              }}
            >
              <span
                className="type-fs-display-m opacity-40"
                style={{ color: heroTextColor }}
              >
                {brand.name.charAt(0)}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );

  if (!showValuePropStrip) return hero;

  return (
    <>
      {hero}
      {/* Full-width sign-up strip: the same statement the in-panel band used
          to carry, at paragraph size, on a warm neutral that complements the
          brand color instead of tinting it. The whole strip is a sign-up gate
          (the page's `data-signup-gate` handler opens the modal), so the CTA
          the band's button used to provide is still reachable without putting
          a button back inside the header. */}
      <div
        data-signup-gate
        role="button"
        tabIndex={0}
        className={cn("w-full cursor-pointer", PADX)}
        style={{ backgroundColor: stripColor, animation: heroFade }}
      >
        <IncentiveLine
          messages={incentives ?? [DEFAULT_INCENTIVE]}
          className="type-fs-paragraph py-3 text-center"
          // White ink once the strip is dark enough to need it — same
          // luminance test the hero band uses, so any strip color a brand
          // picks stays legible without a second manual flag.
          color={
            prefersLightText(stripColor)
              ? "#ffffff"
              : "var(--slate-color-text-primary)"
          }
        />
      </div>
    </>
  );
};

// Current production brand-page header (mirrors the retailer app's BrandHeaderV3:
// full-bleed 6:1 cover, avatar beside name, rating · location · min, Insider
// shipping row, Follow + message + overflow, and the Insider free-shipping card).
const CurrentHeader: React.FC<{
  brand: BrandDataset;
  entryRoute?: string;
}> = ({ brand, entryRoute }) => {
  // The brand's own promos already state its real threshold (e.g. "Free
  // shipping over $150") — read that instead of hardcoding a dollar figure.
  const shippingValue = brand.promos.find((value) =>
    value.toLowerCase().includes("shipping")
  );
  return (
    <div style={{ animation: "pfHomeFade 450ms ease-out both" }}>
      {brand.coverImage ? (
        <div className="bg-fs-border-muted aspect-[6/1] max-h-[340px] min-h-[120px] w-full overflow-hidden">
          <img
            className="h-full w-full object-cover object-center"
            src={brand.coverImage}
            alt={`${brand.name} cover`}
          />
        </div>
      ) : null}

      <div className={cn("flex w-full flex-col", PADX)}>
        <div className="flex items-start justify-between pt-6">
          <div className="flex items-center gap-4">
            <div className="border-fs-border-muted relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-full border">
              <img
                className="h-full w-full object-cover"
                src={brand.avatarUrl}
                alt={brand.name}
              />
            </div>
            <div className="flex flex-col">
              <h1 className="type-fs-section-heading text-fs-text-primary">
                {brand.name}
              </h1>
              <div className="type-fs-paragraph text-fs-text-primary mt-1 flex flex-wrap items-center gap-1">
                {brand.rating ? (
                  <>
                    <span className="inline-flex items-center gap-0.5">
                      <StarFilled className="h-3 w-3" />
                      <span>{brand.rating}</span>
                      {brand.reviewCount ? (
                        <button className="underline">
                          ({brand.reviewCount})
                        </button>
                      ) : null}
                    </span>
                    <span aria-hidden>·</span>
                  </>
                ) : null}
                <span>{brand.location}</span>
                <span aria-hidden>·</span>
                <span>${brand.minOrder} min</span>
              </div>
              <div className="type-fs-paragraph text-fs-text-primary mt-1 flex flex-wrap items-center gap-1">
                {shippingValue ? (
                  <>
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ color: "#0f7b64" }}
                    >
                      <Insider small />
                      <span>Insider</span>
                    </span>
                    <span aria-hidden>·</span>
                  </>
                ) : null}
                <span>Get it by {brand.shipWindow}</span>
                <span aria-hidden>·</span>
                <button className="underline">Shipping details</button>
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2">
            <Button variant="secondary" size="small">
              Follow
            </Button>
            <IconButton
              aria-label="Message brand"
              Icon={ChatBubble}
              size="small"
              variant="secondary"
            />
            <WholesaleMenu
              trigger={
                <IconButton
                  aria-label="More actions"
                  Icon={HorizontalEllipsis}
                  size="small"
                  variant="secondary"
                />
              }
              entryRoute={entryRoute}
            />
          </div>
        </div>

        {shippingValue ? (
          <div className="border-fs-border-muted mt-4 flex w-fit items-center gap-3 rounded-lg border p-4">
            <div className="bg-fs-surface-secondary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full">
              <Insider small width="16" height="16" />
            </div>
            <div className="flex flex-col">
              <span className="type-fs-paragraph-medium text-fs-text-primary">
                {shippingValue} + 5% off with Insider
              </span>
              <button className="type-fs-label text-fs-text-primary w-fit underline">
                Learn more
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Whether a surface is dark enough that white text reads better on it than the
// primary ink. Only answers for literal `rgb()` colors — a CSS var (e.g. a
// Slate surface token) can't be measured here, so those keep the primary ink,
// which is what every Slate surface token is designed for anyway.
const prefersLightText = (color: string): boolean =>
  color.startsWith("rgb(") && !isLightBrandColor(color);

// Whether a brand's own color reads better with dark text/icons than white —
// lets any future brand's hero band (from near-black to a pale blush) always
// get a legible treatment without a manual per-brand light/dark flag.
const isLightBrandColor = (rgbColor: string): boolean => {
  const match = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbColor);
  if (!match) return false;
  const toLinear = (channel: string): number => {
    const normalized = Number(channel) / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * toLinear(match[1]) +
    0.7152 * toLinear(match[2]) +
    0.0722 * toLinear(match[3]);
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithBlack = (luminance + 0.05) / 0.05;
  return contrastWithBlack > contrastWithWhite;
};

/** How long each incentive holds before the next dissolves in. */
const INCENTIVE_HOLD_MS = 4000;

/**
 * The hero's Faire incentive line. Rotates when given more than one.
 *
 * Every line is stacked in one grid cell so the block is always as tall as its
 * longest message and a swap never nudges the hero's layout; the outgoing and
 * incoming lines overlap, which is what makes it a dissolve rather than a blank
 * beat. Same treatment as the brand.com banner, deliberately.
 */
const IncentiveLine: React.FC<{
  messages: string[];
  className: string;
  color: string;
}> = ({ messages, className, color }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (messages.length < 2) return;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % messages.length),
      INCENTIVE_HOLD_MS
    );
    return () => window.clearInterval(timer);
  }, [messages.length]);

  if (messages.length < 2) {
    return (
      <p className={className} style={{ color }}>
        {messages[0] ?? ""}
      </p>
    );
  }

  return (
    <span className="grid">
      {messages.map((message, position) => (
        <p
          key={message}
          aria-hidden={position !== index}
          className={cn(
            "col-start-1 row-start-1 transition-opacity duration-700 ease-in-out motion-reduce:transition-none",
            className,
            position === index ? "opacity-100" : "opacity-0"
          )}
          style={{ color }}
        >
          {message}
        </p>
      ))}
    </span>
  );
};
